/** Building blocks that only the Settings tabs use (Figma "Settings · polished" and its WhatsApp / Notifications frames). */
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { useOrganization } from "@/auth/organization";
import { cx } from "@/components/ui";
import { missingPermissionReason } from "@/lib/access";

const TILE_TONES = {
  neutral: "bg-[#f3ebe7] text-[#5c5551]",
  plan: "bg-[#fef4e5] text-[#995c05]",
  danger: "bg-[#fbd6c9] text-[#d6342f]",
} as const;

/** The 32px tile beside a card's title. */
export function IconTile({
  icon: I,
  tone = "neutral",
}: {
  icon: LucideIcon;
  tone?: keyof typeof TILE_TONES;
}) {
  return (
    <span
      aria-hidden
      className={cx("flex size-8 shrink-0 items-center justify-center rounded-[10px]", TILE_TONES[tone])}
    >
      <I className="size-4" strokeWidth={1.75} />
    </span>
  );
}

/** A card title with its icon tile and an optional trailing element. */
export function PanelHead({
  icon,
  tone,
  title,
  size = "lg",
  children,
}: {
  icon: LucideIcon;
  tone?: keyof typeof TILE_TONES;
  title: ReactNode;
  size?: "lg" | "md";
  children?: ReactNode;
}) {
  return (
    <div className="flex w-full items-center gap-2.5">
      <IconTile icon={icon} tone={tone} />
      <h2
        className={cx(
          "min-w-0 flex-1 font-semibold text-fg",
          size === "lg" ? "text-lg" : "text-base leading-5",
        )}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

/** The 32px tile at the start of a settings row (General tab). */
export function RowIcon({ children, danger = false }: { children: ReactNode; danger?: boolean }) {
  return (
    <span
      aria-hidden
      className={cx(
        "flex size-8 shrink-0 items-center justify-center rounded-[9px]",
        danger ? "bg-[#fff1ee] text-[#d6342f]" : "bg-[#faf6ef] text-[#5c5551]",
      )}
    >
      {children}
    </span>
  );
}

/** A row in a General card: icon, title + detail, trailing control; rows after the first get a rule. */
export function SettingRow({
  icon,
  title,
  detail,
  danger,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  danger?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex w-full items-center gap-3.5 border-border not-first:border-t not-first:pt-3.5">
      <RowIcon danger={danger}>{icon}</RowIcon>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <p className="text-[15px] font-semibold text-fg">{title}</p>
        {detail && <p className="truncate text-[13px] text-fg-subtle">{detail}</p>}
      </div>
      {children}
    </div>
  );
}

/** The Weavex mark, tinted like the other row icons. */
export function WeavexMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx("block bg-current", className)}
      style={{
        maskImage: "url(/icons/notifications/weavex.svg)",
        WebkitMaskImage: "url(/icons/notifications/weavex.svg)",
        maskSize: "100% 100%",
        WebkitMaskSize: "100% 100%",
      }}
    />
  );
}

/**
 * "You can see these settings but not change them. Needs the … role. Ask an owner." under a tab whose
 * fields are read-only for lack of `permission` (QB-057): disabled fields say why in plain sight.
 */
export function ReadOnlyNote({ permission }: { permission: string }) {
  const { can, permissionsLoading } = useOrganization();
  if (permissionsLoading || can(permission)) return null;
  return (
    <p className="text-[13px] text-fg-subtle">
      You can see these settings but not change them. {missingPermissionReason(permission)}
    </p>
  );
}
